
import t from 'react-translate';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import useClearUrlParams from 'athena/hooks/use-clear-url-params';
import { createContext, useCallback, useContext, useState } from 'react';

import {
  SendMentorRequestParams,
  sendMentorRequest,
  UpdateMentorRequestParams,
  updateMentorRequest,
} from 'redux/actions/mentorship-program-connections';
import {
  MENTOR_REQUESTS_PARAMS,
  MentorRequestContextType,
  MentorRequestSource,
  SingleMentorRequestStatus,
  UpdateRequestHandlerType,
} from 'redux/schemas/models/mentor-request';
import { getMentorshipProgramEnrollmentById } from 'redux/selectors/mentorship-program-enrollments';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { useSelector } from 'react-redux';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';

export const MentorRequestContext = createContext<MentorRequestContextType | null>(null);

export const useMentorRequestContext = () => {
  const context = useContext(MentorRequestContext);
  if (!context) {
    return {} as MentorRequestContextType;
  }
  return context;
};

export const MentorRequestProvider = ({
  children,
  value,
}: {
  children: React.ReactNode;
  value: MentorRequestContextType;
}) => {
  const { source, participant, request, requests } = value;
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  const { mentoringProgram } = useContext(MentoringProgramContext);
  const {
    mentorshipProgramEnrollment: enrollmentId,
    programAlias,
    setShowFindMentorModal,
  } = mentoringProgram;
  const mentorshipProgramEnrollment = useSelector(getMentorshipProgramEnrollmentById(enrollmentId));

  const removeParams = useClearUrlParams(MENTOR_REQUESTS_PARAMS);

  const sendRequest = useCallback((data: { message: string }) => {
    const mentorRequestParams: SendMentorRequestParams = {
      message: data.message,
      fullName: participant?.user?.fullName || '',
      mentorEnrollmentId: participant?.id,
      mentorshipProgramId: mentoringProgram.id,
    };
    setLoading(true);
    wrapThunkAction(
      dispatch(sendMentorRequest(mentorRequestParams)),
    ).then(() => {
      if (source === MentorRequestSource.FIND_MENTOR) {
        setShowFindMentorModal(true);
      }
    }).catch((e) => {
      console.warn(e.error?.message);
    }).finally(() => {
      removeParams();
      setLoading(false);
    });
  }, [
    source,
    dispatch,
    participant,
    mentoringProgram.id,
    removeParams,
    setShowFindMentorModal,
  ]);

  const updateRequest = useCallback((state: SingleMentorRequestStatus) => {
    const updateRequestParams: UpdateMentorRequestParams = {
      mentorshipProgramId: mentoringProgram.id,
      mentorRequestId: request?.id,
      menteeEnrollmentId: request?.menteeEnrollmentId,
      state,
    };
    return wrapThunkAction(
      dispatch(updateMentorRequest(updateRequestParams)),
    ).catch((e) => {
      console.warn(e.error?.message);
    }).finally(removeParams) as UpdateRequestHandlerType;
  }, [
    mentoringProgram.id,
    request?.id,
    dispatch,
    removeParams,
  ]);

  const withdrawRequest = useCallback(() => (
    updateRequest(SingleMentorRequestStatus.WITHDRAW)
  ), [updateRequest]);

  const approveRequest = useCallback(() => (
    updateRequest(SingleMentorRequestStatus.APPROVED)
  ), [updateRequest]);

  const rejectRequest = useCallback(() => {
    const promise = updateRequest(SingleMentorRequestStatus.REJECTED);
    promise.then(() => {
      dispatch(
        addAlertMessage({
          header: t.FORM.SUCCESS(),
          type: AlertMessageType.SUCCESS,
          message: t.MENTORING_PROGRAMS.PARTICIPANT_HOME.MENTOR_REQUEST_MODAL.MENTOR_VIEW.REJECT_REQUEST_ALERT_SUCCESS(participant?.user?.firstName || ''),
        })
      );
    });
    return promise;
  }, [updateRequest, participant]);

  const mentorRequestValue: MentorRequestContextType = {
    ...value,
    requests: requests || [request],
    role: mentorshipProgramEnrollment?.role.name,
    aliases: programAlias,
    sendRequest,
    sendingRequest: loading,
    withdrawRequest,
    approveRequest,
    rejectRequest,
  };

  return (
    <MentorRequestContext.Provider value={mentorRequestValue}>
      {children}
    </MentorRequestContext.Provider>
  );
};
